import { EventBanner } from 'components/guide/banner/EventBanner';
import { EventDescription } from 'components/guide/banner/EventDescription';
import { EventMenu } from 'components/guide/menu/EventMenu';
import { MainButton } from 'elements/button/MainButton';
import { PcPageLayout } from 'elements/eventView/layout/PcPageLayout';
import { useHandleMobileUrlOnPc } from 'hooks/event/useHandleMobileUrlOnPc';
import { getDateStringFromEventDays } from 'libs/utils/date/dateUtils';
import { getExternalLinkLocalization } from 'libs/utils/logic/ui/languageLogic';
import { useAnalytics } from 'libs/utils/site/analytics';
import { EVDetailPagePC } from 'pages/EventViewPage/EventViewPagePC/EVDetailPagePC';
import { EVInformationPagePC } from 'pages/EventViewPage/EventViewPagePC/EVInformationPagePC';
import { EVMapPagePC } from 'pages/EventViewPage/EventViewPagePC/EVMapPagePC';
import { EVOtherEntitiesPagePC } from 'pages/EventViewPage/EventViewPagePC/EVOtherEntitiesPagePC';
import { EVProgramPagePC } from 'pages/EventViewPage/EventViewPagePC/EVProgramPagePC';
import { S_PageStyle } from 'pages/page/PageStyle';
import React, { useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const EVPagePC = (props: Props): JSX.Element => {
    const ctxEventView = useContext(CtxEventView);
    const eventInfo = ctxEventView.eventData.eventInfo;
    const [searchParams] = useSearchParams();
    const analytics = useAnalytics();

    useHandleMobileUrlOnPc();

    const detailId = searchParams.get('detailId');
    const detailEntityType = searchParams.get('detailEntityType') as 'program';
    const detailEntityId = searchParams.get('detailEntityId');

    const order = ctxEventView.eventData.eventInfo.order.wide;

    const otherEntitiesContent = ctxEventView.otherEntityDefinitions?.map<
        [string, React.ReactNode]
    >((e) => [e.id, <EVOtherEntitiesPagePC key={e.id} entityDefinition={e} />]);

    const content = {
        program: <>{eventInfo.program && <EVProgramPagePC />}</>,
        information: <>{eventInfo.information && <EVInformationPagePC />}</>,
        ...Object.fromEntries(otherEntitiesContent)
    };

    function getValidLinks() {
        return eventInfo.externalLinks?.filter((externalLink) => {
            return externalLink.url && externalLink.title;
        });
    }

    return (
        <S_EVPagePC className={props.className}>
            <S_EventMenu />
            <S_PCScreen>
                <EventBanner
                    title={eventInfo.name}
                    date={
                        eventInfo.startDateTime
                            ? getDateStringFromEventDays(
                                  eventInfo.startDateTime,
                                  eventInfo.endDateTime
                              )
                            : null
                    }
                    background={eventInfo.headerImageUrl}
                    color={eventInfo.styling?.matchColor}
                />
                {eventInfo.description && <S_EventDescription text={eventInfo.description} />}
                {getValidLinks()?.length > 0 && (
                    <S_Links>
                        {getValidLinks()?.map((externalLink) => {
                            const link = externalLink.url?.startsWith('http')
                                ? externalLink.url
                                : `https://${externalLink.url}`;
                            return (
                                <Link
                                    to={link}
                                    target="_blank"
                                    key={externalLink.url}
                                    onClick={() => {
                                        analytics.track('event_ui.external_link', {
                                            source: 'external_link_list',
                                            destination: externalLink.url
                                        });

                                        window.open(link, '_blank', 'noreferrer');
                                    }}
                                >
                                    <MainButton
                                        type="event"
                                        iconSet="far"
                                        iconName="arrow-up-right-from-square"
                                        backgroundColor={eventInfo.externalLinkButtonBackground}
                                        textColor={eventInfo.externalLinkButtonTextColor}
                                    >
                                        {getExternalLinkLocalization(externalLink.title)}
                                    </MainButton>
                                </Link>
                            );
                        })}
                    </S_Links>
                )}
            </S_PCScreen>
            <S_PcPageLayout>
                {order.map((key) => {
                    if (ctxEventView.eventData.eventInfo.homepageContent?.[key]) {
                        return ctxEventView.eventData.eventInfo.homepageContent?.[key]?.();
                    }

                    return <React.Fragment key={key}>{content[key]}</React.Fragment>;
                })}
                {eventInfo.hasMap && <EVMapPagePC />}
            </S_PcPageLayout>
            {detailId && <S_EVDetailPagePCSingle detailId={detailId} />}
            {detailEntityType && detailEntityId && (
                <S_EVDetailPagePCMultiple
                    detailList={{ entity: detailEntityType, entityId: detailEntityId }}
                />
            )}
        </S_EVPagePC>
    );
};

const S_EVPagePC = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
    background: #f6f9ff;
`;

const S_EventDescription = styled(EventDescription)`
    margin: 20px 0 0 0;
`;

const S_PcPageLayout = styled(PcPageLayout)`
    margin-bottom: 150px;
`;

const S_PCScreen = styled.div`
    margin: 120px auto 0 auto;
    width: 1260px;
`;

const S_EVDetailPagePCSingle = styled(EVDetailPagePC)`
    z-index: 400;
`;

const S_EVDetailPagePCMultiple = styled(EVDetailPagePC)`
    z-index: 300;
`;

const S_EventMenu = styled(EventMenu)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 100;
`;

const S_Links = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    margin: 20px 0 0 0;
    justify-content: center;
`;
